import React, { createContext, useState, useContext, useRef, useCallback, useMemo } from "react";
import { ShareDocModal } from "@/appComponents/Blocks/Modals/ShareDocModal/ShareDocModal";
import { GatedModals } from "@/appComponents/Blocks/Composites/ModalHost/GatedModals";
import * as StateLib from "@/Utils/StateLib";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumBGOption } from "@coreapp/constants";
import { SubscribeExternalModuleReactAPI } from "@coreapp/main";

const WsMgrContext = createContext();

const WsMgrProvider = ({ children }) => {
  const nav = useAppNavigate();
  const [wsMgr, setWsMgr] = useState({ state: Globals._wsMgr });

  const [alertQueueState, setAlertQueueState] = useState({
    alertQueue: []
  });

  const enqueueAlertInfoObject = useCallback(alertInfo => {
    setAlertQueueState(prevState => ({
      alertQueue: [...prevState.alertQueue, alertInfo]
    }));
  }, []);

  const processAlertQueue = useCallback(() => {
    setAlertQueueState(prevState => ({
      alertQueue: prevState.alertQueue.slice(1)
    }));
  }, []);

  const handleAlertEvent = useCallback(
    eventData => {
      const alertInfo = createAlertInfoObject(eventData);
      enqueueAlertInfoObject(alertInfo);
    },
    [enqueueAlertInfoObject]
  );

  const dismissAlertById = useCallback(id => {
    setAlertQueueState(prevState => ({
      alertQueue: prevState.alertQueue.filter(alert => alert.id !== id)
    }));
  }, []);

  const createAlertInfoObject = eventData => {
    return {
      ...eventData,
      id: eventData.id ?? null
    };
  };

  const alertContextValue = useMemo(
    () => ({
      alertQueue: alertQueueState.alertQueue,
      enqueueAlertInfoObject,
      processAlertQueue,
      handleAlertEvent
    }),
    [alertQueueState.alertQueue, enqueueAlertInfoObject, processAlertQueue, handleAlertEvent]
  );

  const [showShareModal, setShowShareModal] = useState(false);
  const [onSaveShareFn, setOnSaveShareFn] = useState({ function: null });
  const [shareData, setShareData] = useState(null);

  const closeShareModal = useCallback(() => {
    setShowShareModal(false);
    setOnSaveShareFn({ function: null });
    setShareData(null);
  }, []);

  const openShareModal = useCallback(onSaveFn => {
    setShowShareModal(true);
    setOnSaveShareFn({ function: onSaveFn });
  }, []);

  const onSaveShare = useCallback(
    data => {
      if (onSaveShareFn?.function) onSaveShareFn.function(data);
      closeShareModal();
    },
    [onSaveShareFn, closeShareModal]
  );

  const shareContextValue = useMemo(
    () => ({
      showShareModal,
      setOnSaveShareFn,
      shareData,
      setShareData,
      openShareModal,
      closeShareModal,
      onSaveShare
    }),
    [showShareModal, shareData, openShareModal, closeShareModal, onSaveShare]
  );

  const [showBuilder, setShowBuilder] = useState(false);
  const [initialSettings, setInitialSettings] = useState({});

  const createCustomChart = useCallback(
    builderInfo => {
      setShowBuilder(false);
      setInitialSettings({});
      const workspaceMgr = StateLib.GetWSMgr();
      const doc = StateLib.GetDoc();

      if (doc == null) return;
      Globals._svrChartMgr.PersistFH(doc);
      const newChartID = doc.GetChartID();
      const iWizData = doc.GetIWizData();
      const isLinkedBackground = iWizData.GetMasterOption() === enumBGOption.linked;
      builderInfo.mtemplate = isLinkedBackground ? iWizData.GetMasterFile() : null;
      builderInfo._pendoChartType = doc.IsPositionDocument() ? "position" : "employee";
      const newDoc = workspaceMgr.InitializeDoc({}, false, true);
      workspaceMgr.fBuilderInfo = builderInfo;
      workspaceMgr.GetChartMetadata(newDoc, newChartID);

      const oi = workspaceMgr.GetOrgInfo();
      const prevSetting = oi.GetOpenDoc();
      oi.SetOpenDoc(false);
      nav.setMode("edit", { replace: true });
      oi.SetOpenDoc(prevSetting);
    },
    [nav]
  );

  const openBuilder = useCallback(initialSettings => {
    if (initialSettings) {
      setInitialSettings(initialSettings);
    }
    setShowBuilder(true);
  }, []);

  const closeBuilder = useCallback(() => {
    setShowBuilder(false);
    setInitialSettings({});
  }, []);

  const builderContextValue = useMemo(
    () => ({
      initialSettings,
      setInitialSettings,
      showBuilder,
      openBuilder,
      closeBuilder,
      createCustomChart
    }),
    [initialSettings, showBuilder, openBuilder, closeBuilder, createCustomChart]
  );

  React.useEffect(() => {
    SubscribeExternalModuleReactAPI(setWsMgr, "wsmgr");
  }, []);

  React.useEffect(() => {
    if (wsMgr && wsMgr["state"] != null) {
      wsMgr["state"].handleAlert = handleAlertEvent;
      wsMgr["state"].processAlertQueue = processAlertQueue;
    }
  }, [wsMgr, handleAlertEvent]);

  const contextValue = useMemo(
    () => ({
      dismissAlertById,
      wsMgr,
      setWsMgr,
      alertContextValue,
      shareContextValue,
      builderContextValue
    }),
    [wsMgr, dismissAlertById, alertContextValue, shareContextValue, builderContextValue]
  );

  return (
    <WsMgrContext.Provider value={contextValue}>
      <GatedModals>
        <ShareDocModal />
      </GatedModals>
      {children}
    </WsMgrContext.Provider>
  );
};

export const useWSMgrContext = () => useContext(WsMgrContext);

export default WsMgrProvider;
